import { useTranslations } from "adapters/i18n";
import { useConfirmDialog } from "features/shared/hooks/useConfirmDialog";
import { useState } from "react";
import type { NotifyFn } from "../../../userManagement.types";
import userService from "../../../userService";
import type { User } from "../userList.types";

type TargetUser = {
  isActive: boolean;
  userId: number;
};

type UseChangeUserStatusProps = {
  clientId: number | null;
  onNotify: NotifyFn;
  toggleLoading: (isLoading: boolean) => void;
};

export function useChangeUserStatus({
  clientId,
  onNotify,
  toggleLoading,
}: UseChangeUserStatusProps) {
  const { t } = useTranslations();

  const [currentUser, setCurrentUser] = useState<TargetUser | null>(null);
  const [lastUpdate, setLastUpdate] = useState(new Date().getTime());

  const changeUserStatus = async (isActive: boolean, userId: number) => {
    try {
      toggleLoading(true);

      if (isActive) {
        await userService.inactivateUser(userId, clientId);
        onNotify(t("user:user-successfully-inactivated"), "success");
        return;
      }

      await userService.activateUser(userId, clientId);
      onNotify(t("user:user-successfully-activated"), "success");
    } catch (err) {
      console.error(err);
      onNotify(t("user:user-status-change-error"), "error");
    } finally {
      setLastUpdate(new Date().getTime());
      toggleLoading(false);
    }
  };

  const handleConfirmStatusChange = () => {
    if (!currentUser) {
      return;
    }

    changeUserStatus(currentUser.isActive, currentUser.userId);
    setCurrentUser(null);
  };

  const {
    render: renderInactivateUserDialog,
    setVisible: setIsInactivateUserDialogOpen,
  } = useConfirmDialog({
    title: t("user:inactivate-user-modal.title"),
    text: t("user:inactivate-user-modal.description"),
    confirmButtonText: t("common:actions.yes-confirm"),
    onConfirm: handleConfirmStatusChange,
  });

  const handleToggleUserStatus = async (user: User) => {
    const isActive = user.isActiveForClient;

    if (!isActive) {
      await changeUserStatus(isActive, user.id);
      return;
    }

    setCurrentUser({ isActive, userId: user.id });
    setIsInactivateUserDialogOpen(true);
  };

  return {
    handleToggleUserStatus,
    lastUpdate,
    renderInactivateUserDialog,
  };
}
